import { TruncatedText } from "@/components/ui/truncation-tooltip";
import type { FileResponse, ReconciliationSet } from "@efes/shared";
import { AlertTriangle } from "lucide-react";
import { useEffect, useId, useMemo, useRef } from "react";

interface BulkDeleteConfirmDialogProps {
	files: FileResponse[];
	/**
	 * Список наборов сверки. Используется для предупреждения, если хотя бы один
	 * из удаляемых файлов входит в набор. Может быть пустым массивом, если данных
	 * нет — в этом случае предупреждение не показывается.
	 */
	reconciliationSets: ReconciliationSet[];
	onConfirm: () => void;
	onCancel: () => void;
	isPending?: boolean;
}

const fileTypeLabels: Record<string, string> = {
	work_order: "Наряд",
	completion_act: "Акт",
	rate_book: "Книга расценок",
	estimate: "Смета",
	unclassified: "Не определён",
};

/**
 * Модальный диалог подтверждения группового удаления.
 *
 * Показывает:
 * 1. Список выбранных файлов с типами.
 * 2. Предупреждение об affected reconciliation sets (если есть пересечения).
 * 3. Текст безотзывности и кнопки [Отмена] [Удалить N].
 *
 * Реконцилиация: для каждого набора, который содержит хотя бы один из удаляемых
 * файлов, перечисляем его имя и сколько именно его файлов удалится. После
 * удаления набор остаётся в БД, но preflight перестаёт проходить и потребуется
 * либо добавить новые файлы, либо удалить набор.
 */
export function BulkDeleteConfirmDialog({
	files,
	reconciliationSets,
	onConfirm,
	onCancel,
	isPending = false,
}: BulkDeleteConfirmDialogProps) {
	const titleId = useId();
	const dialogRef = useRef<HTMLDialogElement>(null);

	useEffect(() => {
		const el = dialogRef.current;
		if (el && !el.open) el.showModal();
	}, []);

	useEffect(() => {
		const el = dialogRef.current;
		if (!el) return;
		const handler = (e: Event) => {
			e.preventDefault();
			if (!isPending) onCancel();
		};
		el.addEventListener("cancel", handler);
		return () => el.removeEventListener("cancel", handler);
	}, [onCancel, isPending]);

	const affectedSets = useMemo(() => {
		const fileIds = new Set(files.map((f) => f.id));
		const result: Array<{ set: ReconciliationSet; affectedFiles: number }> = [];
		for (const set of reconciliationSets) {
			const woMatches = set.files.workOrders.filter((f) => fileIds.has(f.id)).length;
			const actMatches = set.files.acts.filter((f) => fileIds.has(f.id)).length;
			const total = woMatches + actMatches;
			if (total > 0) {
				result.push({ set, affectedFiles: total });
			}
		}
		return result;
	}, [files, reconciliationSets]);

	// Sprint 2 / A3 (FSM): наборы со статусом 'running' блокируют удаление —
	// бэкенд отдаёт 409 FILE_IN_RUNNING_SET. Гасим кнопку и показываем красный
	// блок предупреждения, чтобы пользователь дождался завершения прогона.
	const blockingSets = useMemo(
		() => affectedSets.filter(({ set }) => set.status === "running"),
		[affectedSets],
	);
	const hasBlocking = blockingSets.length > 0;

	const count = files.length;

	return (
		<dialog
			ref={dialogRef}
			className="fixed inset-0 m-auto w-[calc(100%-2rem)] max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-lg bg-white p-6 shadow-xl backdrop:bg-black/50"
			aria-labelledby={titleId}
		>
			<h3 id={titleId} className="mb-1 text-lg font-semibold text-slate-900">
				Удалить {count} {pluralize(count, "файл", "файла", "файлов")}?
			</h3>
			<p className="mb-3 text-sm text-slate-600">
				Это действие необратимо. Связанные строки нарядов, актов, расценки и записи в наборах
				сверки будут удалены каскадно.
			</p>

			<div className="mb-4 max-h-48 overflow-y-auto rounded-md border border-slate-200 bg-slate-50">
				<ul className="divide-y divide-slate-200">
					{files.map((f) => (
						<li
							key={f.id}
							className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-700"
						>
							<span className="shrink-0 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium uppercase text-slate-700">
								{fileTypeLabels[f.fileType] ?? f.fileType}
							</span>
							<span className="min-w-0 flex-1">
								<TruncatedText value={f.originalName} />
							</span>
						</li>
					))}
				</ul>
			</div>

			{affectedSets.length > 0 && (
				<div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
					<div className="mb-1.5 flex items-center gap-1.5 font-semibold">
						<AlertTriangle className="h-4 w-4" />
						Затронуты наборы сверки
					</div>
					<ul className="space-y-0.5 pl-5 list-disc">
						{affectedSets.map(({ set, affectedFiles }) => {
							const isRunning = set.status === "running";
							return (
								<li key={set.id} className={isRunning ? "font-semibold text-red-700" : ""}>
									«{set.name}»
									{isRunning && (
										<span className="ml-1 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-red-700">
											Сверка идёт
										</span>
									)}{" "}
									— потеряет{" "}
									{pluralize(affectedFiles, "файл", "файла", "файлов")}
								</li>
							);
						})}
					</ul>
					<p className="mt-2 text-xs">
						После удаления preflight таких наборов перестанет проходить — потребуется
						добавить новые файлы или удалить набор.
					</p>
				</div>
			)}

			{hasBlocking && (
				<div className="mb-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">
					<div className="mb-1.5 flex items-center gap-1.5 font-semibold">
						<AlertTriangle className="h-4 w-4" />
						Удаление невозможно
					</div>
					<p className="text-xs">
						Часть выбранных файлов входит в наборы с активной сверкой. Дождитесь
						завершения прогона или отмените его, затем повторите удаление.
					</p>
				</div>
			)}

			<div className="flex justify-end gap-2">
				<button
					type="button"
					onClick={onCancel}
					disabled={isPending}
					className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
				>
					Отмена
				</button>
				<button
					type="button"
					onClick={onConfirm}
					disabled={isPending || hasBlocking}
					className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
				>
					{isPending
						? "Удаление…"
						: `Удалить ${count} ${pluralize(count, "файл", "файла", "файлов")}`}
				</button>
			</div>
		</dialog>
	);
}

/** Простое склонение для русских числительных: 1 файл / 2 файла / 5 файлов. */
function pluralize(n: number, one: string, few: string, many: string): string {
	const mod10 = n % 10;
	const mod100 = n % 100;
	if (mod10 === 1 && mod100 !== 11) return one;
	if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
	return many;
}
